"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useConvexAuth, useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useSignupConversion } from "@/hooks/useSignupConversion";
import { AnalyticsEvent, trackEvent } from "@/lib/analytics";
import WelcomeModal from "@/components/WelcomeModal";
import SupportModal from "@/components/SupportModal";

/** Per-user completion flag for the post-onboarding welcome modal (users.completedTours). */
export const WELCOME_MODAL_ID = "welcome-modal";

interface HelpContextValue {
  /** Open the three-screen welcome/intro modal. */
  openWelcome: () => void;
  /** Open the contact-support modal (user menu "Help"). */
  openSupport: () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function useHelp() {
  const context = useContext(HelpContext);
  if (!context) {
    throw new Error("useHelp must be used within HelpProvider");
  }
  return context;
}

/**
 * Mounts the welcome + support modals app-wide and auto-opens the welcome
 * modal when the user lands on /dashboard?tour=main after onboarding (the
 * query param is kept for compatibility with the onboarding redirects that
 * predate the driver.js tour removal).
 */
export function HelpProvider({ children }: { children: React.ReactNode }) {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const tourParam = searchParams.get("tour");

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  // Gate on Convex auth, not Clerk's isSignedIn (sign-out race, see src/lib/authError.ts)
  const { isAuthenticated } = useConvexAuth();
  const isWelcomeCompleted = useQuery(
    api.tours.isTourCompleted,
    isAuthenticated ? { tourId: WELCOME_MODAL_ID } : "skip"
  );
  const markTourCompleted = useMutation(api.tours.markTourCompleted);

  const isNewCustomerDashboardLanding =
    tourParam === "main" && pathname === "/dashboard";

  useSignupConversion(isNewCustomerDashboardLanding);

  // Fire "Dashboard - New Customer" once when the user is redirected to the
  // dashboard for the first time after completing onboarding (?tour=main).
  const dashboardEventFiredRef = useRef(false);
  useEffect(() => {
    if (isNewCustomerDashboardLanding && !dashboardEventFiredRef.current) {
      dashboardEventFiredRef.current = true;
      trackEvent(AnalyticsEvent.DashboardNewCustomer);
    }
  }, [isNewCustomerDashboardLanding]);

  // Auto-open the welcome modal when ?tour=main is in the URL (after
  // onboarding) and this user hasn't seen it yet.
  useEffect(() => {
    if (isNewCustomerDashboardLanding && isWelcomeCompleted === false) {
      // Small delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        setWelcomeOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isNewCustomerDashboardLanding, isWelcomeCompleted]);

  const handleWelcomeClose = () => {
    setWelcomeOpen(false);
    // Skip and "Get started" both count as seen; the modal shows once.
    if (isAuthenticated && isWelcomeCompleted === false) {
      markTourCompleted({ tourId: WELCOME_MODAL_ID });
    }
  };

  return (
    <HelpContext.Provider
      value={{
        openWelcome: () => setWelcomeOpen(true),
        openSupport: () => setSupportOpen(true),
      }}
    >
      {children}
      <WelcomeModal isOpen={welcomeOpen} onClose={handleWelcomeClose} />
      <SupportModal
        isOpen={supportOpen}
        onClose={() => setSupportOpen(false)}
        onReplayIntro={() => {
          setSupportOpen(false);
          setWelcomeOpen(true);
        }}
      />
    </HelpContext.Provider>
  );
}
